// package imports
import { useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';
import SubdirectoryArrowRightOutlinedIcon from '@mui/icons-material/SubdirectoryArrowRightOutlined';
import { useTheme } from '@mui/material/styles';
import { Theme as MuiTheme } from '@mui/material/styles';
import { KitButton } from 'components/common/cms-kit';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';

// local imports
import { readNDJSONStream } from 'services/clientApiUtils';
import UserMessage from './UserMessage';
import AssistantMessage from './AssistantMessage';
import ErrorMessage from './ErrorMessage';
import { generateUUID } from 'services/stringUtils';
import { logClientException } from 'appinsights/clientAppInsights';
import Sidebar from './Sidebar';
import { useSearchParams } from 'services/hooks/useSearchParams';
import { useFetchConversation } from './hooks/conversationHistoryhooks';
import {
  SystemLimitExceeded,
  UserLimitExceeded,
} from 'services/smartChatUtils/smartChatErrors';
import { AIChatCompletionDelta } from '@microsoft/ai-chat-protocol';
import { AgilityPageProps } from 'types/AgilityTypes';
import { ConversationState, Message, RagChatFields } from './types';
import { newConversation } from './commonUtils';
import CommonQuestions from './CommonQuestions';
import AIDisclaimer from 'components/common/AIDisclaimer';

const RAGChat = ({ module }: AgilityPageProps<RagChatFields>) => {
  const fields = module.fields;

  const router = useRouter();
  const queryClient = useQueryClient();

  const [question, setQuestion] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [conversationState, setConversationState] =
    useState<ConversationState | null>(null);
  const textBoxRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme() as MuiTheme;
  const searchId = useSearchParams('cid');

  const { data, status, error } = useFetchConversation(searchId);

  useEffect(() => {
    if (status === 'success' && data && data.messages) {
      if (data.id === 'new') {
        resetConversation();
      } else {
        setConversationState({
          id: data.id,
          startTime: data.startTime,
          question: data?.messages[0]?.content || '',
        });
        setMessages(data?.messages || []);
      }
      scrollContainer();
    } else if (status === 'error') {
      logClientException(error);
    }
  }, [status, data, error]);

  const scrollContainer = () => {
    if (
      containerRef?.current &&
      typeof window !== 'undefined' &&
      containerRef.current.getBoundingClientRect().top > 133
    ) {
      window.scrollTo({
        top: containerRef.current.offsetTop - 133,
        behavior: 'smooth',
      });
      console.log('scrolled to top of container');
    }
  };

  const sendChatMessage = async (q: string) => {
    let id: NodeJS.Timeout | null = null;
    const startTime = new Date().getTime();
    if (messages.length === 0) {
      scrollContainer();
    }

    try {
      if (loading) {
        return;
      }
      let convContext = conversationState;
      if (!convContext) {
        convContext = {
          id: generateUUID(),
          startTime: new Date().toISOString(),
          question: q,
        };
        setConversationState(convContext);
      }
      const reqBody = {
        messages: [...messages, { content: q, role: 'user' }],
        sessionState: 'TODO',
        context: convContext,
      };

      setLoading(true);
      setMessages((prev) => [
        ...prev,
        { content: q, role: 'user', sessionState: reqBody.sessionState },
      ]);

      const message: Message = {
        messageId: generateUUID(),
        content: '',
        role: 'assistant',
        followUpQuestions: [],
        sessionState: reqBody.sessionState,
      };
      setMessages((prev) => [...prev, message]);

      const timeout = 60000;
      const controller = new AbortController();
      id = setTimeout(() => controller.abort('timeout'), timeout);

      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reqBody),
        signal: controller.signal,
      };
      const response = await fetch('/api/search/chat', options);
      if (!response.ok) {
        if (response.status === 500) {
          const chatError = await response.json();
          if (chatError.message) {
            throw chatError;
          }
        }
        throw new Error(`Chat request failed: ${response.status}`);
      }
      if (!response.body) {
        throw new Error('Chat request failed: no response body');
      }

      for await (const chunk of readNDJSONStream<AIChatCompletionDelta>(
        response.body,
      )) {
        if (chunk.delta.content) {
          setMessages((prev) => {
            message.content += chunk.delta.content;
            return prev.map((m) =>
              m.messageId === message.messageId
                ? { ...m, content: message.content }
                : m,
            );
          });
          if (textBoxRef.current && typeof window !== 'undefined') {
            window.setTimeout(() => {
              if (
                textBoxRef.current &&
                textBoxRef.current?.getBoundingClientRect().bottom >=
                  (window.innerHeight || document.documentElement.clientHeight)
              ) {
                textBoxRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'end',
                });
              }
            }, 250);
          }
        }
      }
    } catch (error: any) {
      if (
        error.name === UserLimitExceeded.name ||
        error.name === SystemLimitExceeded.name
      ) {
        setMessages((prev) => [
          ...prev.slice(0, prev.length - 1),
          { content: error.message, role: 'error' },
        ]);
      } else {
        console.error('Error sending question:', error);
        logClientException(error);
        setMessages((prev) => [
          ...prev.slice(0, prev.length - 1),
          {
            content:
              'Sorry, I am unable to answer that question at the moment. Please try again later.',
            role: 'error',
          },
        ]);
      }
    } finally {
      setLoading(false);
      if (id) {
        clearTimeout(id);
      }
      console.log(
        `sendChatMessage done in ${new Date().getTime() - startTime}ms`,
      );
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setConversationState(null);
    setQuestion('');
  };

  return (
    <div
      id="rag-chat-container"
      className="flex flex-col items-center justify-center mb-4"
    >
      <div
        ref={containerRef}
        id="grid-container"
        className="grid 
        grid-rows-auto
        grid-flow-row
        custom1200:grid-cols-5 custom1200:grid-rows-1 
        justify-center gap-4 custom900:mt-1 mt-6 ml-0 md:ml-4 mr-0 md:mr-4 sm:max-w-full lg:max-w-[1180px]"
      >
        <div
          id="rag-sidebar"
          className="p-1 custom1200:col-span-1 custom1200:row-span-2"
        >
          <>
            <div className="flex custom1200:hidden gap-4 justify-between px-4">
              <IconButton onClick={() => setDrawerOpen(true)}>
                <HistoryOutlinedIcon
                  sx={{ color: theme.palette.text.dark }}
                  height="24"
                  width="24"
                />
                <span
                  style={{
                    fontSize: '18px',
                    lineHeight: '23.4px',
                    fontWeight: 400,
                    color: theme.palette.text.dark,
                    marginLeft: '.2rem',
                  }}
                >
                  {' '}
                  History
                </span>
              </IconButton>
              <KitButton
                style={{
                  backgroundColor: theme.palette.primary.light2,
                }}
                round
                size="sm"
                onClick={() =>
                  newConversation(queryClient, router, resetConversation)
                }
                title="New Conversation"
              >
                <div className="flex flex-row justify-between gap-2">
                  New Conversation <MapsUgcOutlinedIcon />
                </div>
              </KitButton>
            </div>
            <Sidebar
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              reset={resetConversation}
            />
          </>
        </div>

        <div
          id="rag-chat-box"
          className={`custom1200:col-span-4 m-0 md:m-40px rounded-lg ${messages.length === 0 ? 'p-10' : 'p-6'} bg-blue-tint`}
        >
          {messages?.map((message, index) =>
            message.role === 'user' ? (
              <UserMessage key={index} index={index} message={message} />
            ) : message.role === 'assistant' ? (
              <AssistantMessage
                key={index}
                index={index}
                message={message}
                handleFollowUp={sendChatMessage}
              />
            ) : (
              <ErrorMessage key={index} index={index} message={message} />
            ),
          )}
          {messages.length === 0 && (
            <div
              id="rag-chat-title"
              className="flex justify-center items-center text-h-xl font-semibold text-black"
            >
              {fields.questionTitle}
            </div>
          )}
          <TextField
            label="Ask a question..."
            id="rag-chat-question"
            name="rag-chat-question"
            fullWidth
            multiline={messages.length === 0 ? true : false}
            rows={messages.length === 0 ? 4 : 1}
            value={question}
            disabled={loading}
            onChange={(e) => setQuestion(e.target.value)}
            sx={{
              marginTop: '1rem',
              '& .MuiInputBase-root': {
                backgroundColor: theme.palette.common.white,
              },
              '& .MuiOutlinedInput-root': { borderRadius: '.375rem' },
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                if (!question || question.trim() === '') {
                  return;
                }
                sendChatMessage(question);
                setQuestion('');
                e.preventDefault();
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{
                      position: 'absolute',
                      right: '15px',
                      bottom: '20px',
                    }}
                  >
                    <IconButton
                      ref={textBoxRef}
                      disabled={loading}
                      aria-label="send question"
                      edge="end"
                      onClick={() => {
                        if (!question || question.trim() === '') {
                          return;
                        }
                        sendChatMessage(question);
                        setQuestion('');
                      }}
                    >
                      <SubdirectoryArrowRightOutlinedIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          <div>
            {fields.aiDisclaimerLinkText && (
              <div className="text-xs text-gray-600 mt-3 text-center md:flex md:justify-center md:items-center ">
                <AIDisclaimer
                  linkText={fields.aiDisclaimerLinkText}
                  disclaimer={fields.aiDisclaimer || ''}
                />
              </div>
            )}
          </div>
        </div>
        <CommonQuestions
          commonlyAskedQuestionList={fields.commonlyAskedQuestionList}
          commonQuestionTitle={fields.commonQuestionTitle}
          sendChatMessage={sendChatMessage}
          hideCommonQuestions={messages.length > 0}
        />
      </div>
    </div>
  );
};

export default RAGChat;
